class FinishCheckoutController {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;
  }

  getFinishQuantity() {
    const { finishQtyInput } = this.orchestrator.dom;
    const value = Number.parseInt(finishQtyInput?.value ?? '1', 10);
    if (!Number.isFinite(value) || value < 1) return 1;
    return Math.min(value, 99);
  }

  setFinishQuantity(nextValue) {
    const { finishQtyInput } = this.orchestrator.dom;
    if (!finishQtyInput) return;
    const clamped = Math.min(Math.max(Number(nextValue) || 1, 1), 99);
    finishQtyInput.value = String(clamped);
  }

  syncFinishProductColor() {
    const { finishProductColorEl } = this.orchestrator.dom;
    if (!finishProductColorEl) return;
    finishProductColorEl.textContent =
      this.orchestrator.selectionState.getSelectedColor()?.label || 'Rosa';
  }

  syncFinishCustomizationSummary() {
    const { selectionState } = this.orchestrator;
    const {
      reviewStyleEl,
      reviewMethodEl,
      reviewArtRowEl,
      reviewArtEl,
      reviewTextRowEl,
      reviewTextLabelEl,
      reviewTextEl,
      reviewLayoutRowEl,
      reviewLayoutEl,
      reviewFontRowEl,
      reviewFontEl,
      reviewColorRowEl,
      reviewColorLabelEl,
      reviewColorEl,
      reviewBottleColorEl,
    } = this.orchestrator.dom;
    const art = selectionState.getSelectedArt();
    const customConfig = this.orchestrator.getFontCustomConfig(
      art?.textCompatible,
    );
    const text = selectionState.requiresText()
      ? selectionState.customText.trim()
      : '';
    const hasText = selectionState.requiresText();
    const hasArt = selectionState.requiresArt();
    const styleLabels = {
      text: 'Texto',
      mono: 'Monograma',
      textprint: 'Texto + Estampa',
      prints: 'Estampas',
    };
    if (reviewStyleEl) reviewStyleEl.textContent = styleLabels[selectionState.style] || '—';
    if (reviewMethodEl) reviewMethodEl.textContent = selectionState.method === 'laser' ? 'Laser' : 'Tinta';
    if (reviewArtRowEl) reviewArtRowEl.hidden = !hasArt;
    if (reviewArtEl) reviewArtEl.textContent = art?.name || '—';
    if (reviewTextRowEl) reviewTextRowEl.hidden = !hasText;
    if (reviewTextLabelEl) reviewTextLabelEl.textContent = selectionState.style === 'mono' ? 'Iniciais' : 'Texto';
    if (reviewTextEl) reviewTextEl.textContent = text || '—';
    if (reviewLayoutRowEl) reviewLayoutRowEl.hidden = !hasText;
    if (reviewLayoutEl) reviewLayoutEl.textContent = selectionState.layout === 'initials' ? 'Iniciais' : 'Nome completo';
    if (reviewFontRowEl) reviewFontRowEl.hidden = !hasText;
    if (reviewFontEl) {
      reviewFontEl.textContent = customConfig
        ? `${customConfig.initial.name} + ${customConfig.name.name}`
        : selectionState.getSelectedFont()?.name || '—';
    }
    if (reviewColorRowEl) reviewColorRowEl.hidden = !hasText;
    if (reviewColorLabelEl) {
      reviewColorLabelEl.textContent = customConfig?.colorUi === 'dual'
        ? 'Cores do texto'
        : selectionState.method === 'laser'
          ? 'Cor da gravação'
          : 'Cor do texto';
    }
    if (reviewColorEl) {
      reviewColorEl.textContent = customConfig?.colorUi === 'dual'
        ? `Cor da inicial · ${this.orchestrator.textCustomizationController.getColorName(selectionState.selectedInitialTextColor)} • Cor do nome · ${this.orchestrator.textCustomizationController.getColorName(selectionState.selectedNameTextColor)}`
        : selectionState.method === 'laser'
          ? (selectionState.laserColor === 'dourado' ? 'Dourado' : 'Prata')
          : this.orchestrator.textCustomizationController.getColorName(selectionState.inkColor);
    }
    if (reviewBottleColorEl) reviewBottleColorEl.textContent = selectionState.getSelectedColor()?.label || '—';
  }

  syncAcceptanceButtons() {
    const { selectionState } = this.orchestrator;
    const { reviewAccepted, finishAddCartBtn, finishCheckoutQrBtn } = this.orchestrator.dom;
    if (reviewAccepted) reviewAccepted.checked = selectionState.accepted;
    [finishAddCartBtn, finishCheckoutQrBtn].forEach(button => {
      if (!button) return;
      button.disabled = !selectionState.accepted;
      button.setAttribute('aria-disabled', selectionState.accepted ? 'false' : 'true');
    });
  }

  syncFinishStep() {
    this.syncFinishProductColor();
    this.syncFinishCustomizationSummary();
    this.setFinishQuantity(this.getFinishQuantity());
    this.syncAcceptanceButtons();
  }

  buildCustomizationProperties() {
    const { selectionState } = this.orchestrator;
    const customConfig = this.orchestrator.getFontCustomConfig(
      selectionState.getSelectedArt()?.textCompatible,
    );
    const styleLabels = {
      text: 'Texto',
      mono: 'Monograma',
      textprint: 'Texto + Estampa',
      prints: 'Estampas',
    };
    return {
      style: styleLabels[selectionState.style] || '',
      method: selectionState.method === 'laser' ? 'Laser' : 'Tinta',
      layout: selectionState.requiresText()
        ? (selectionState.layout === 'initials' ? 'Iniciais' : 'Nome completo')
        : null,
      font: selectionState.requiresText()
        ? customConfig
          ? `${customConfig.initial.name} + ${customConfig.name.name}`
          : selectionState.getSelectedFont()?.name || ''
        : null,
      laserColor: selectionState.method === 'laser'
        ? (selectionState.laserColor === 'dourado' ? 'Dourado' : 'Prata')
        : null,
    };
  }

  buildShopifyCheckoutUrl({
    preview = null,
    thumb = null,
    productSku = null,
    textProperties = null,
  } = {}) {
    const { selectionState, shopifyCartController, DEFAULT_PRODUCT_SKU } =
      this.orchestrator;
    const variantId = selectionState.getSelectedColor()?.variantId;
    if (!variantId) return null;

    return shopifyCartController.buildCheckoutUrl({
      variantId,
      quantity: this.getFinishQuantity(),
      productSku:
        productSku ||
        selectionState.getSelectedArt()?.sku ||
        DEFAULT_PRODUCT_SKU,
      textProperties: textProperties || this.buildTextProperties(),
      preview,
      thumb,
      artName: selectionState.getSelectedArt()?.name || null,
      customization: this.buildCustomizationProperties(),
    });
  }

  buildCheckoutQrImageUrl(checkoutUrl) {
    const params = new URLSearchParams({
      size: '240x240',
      data: checkoutUrl,
    });
    return `${this.orchestrator.QR_CODE_API_URL}?${params.toString()}`;
  }

  closeCheckoutQrOverlay() {
    const { checkoutQrOverlay } = this.orchestrator.dom;
    if (!checkoutQrOverlay) return;
    checkoutQrOverlay.hidden = true;
  }

  async openCheckoutQrOverlay() {
    const { checkoutQrOverlay, checkoutQrImage, checkoutQrLink, modalWrapper } =
      this.orchestrator.dom;
    if (!checkoutQrOverlay) return;

    modalWrapper?.classList.add('customizer-generated');

    try {
      const image =
        this.orchestrator.bottleSceneController.captureFrontPreviewImageDataUrl();

      const { preview, thumb, productSku, textProperties } =
        await this.savePreviewImage(image);
      const checkoutUrl = this.buildShopifyCheckoutUrl({
        preview,
        thumb,
        productSku,
        textProperties,
      });

      if (!checkoutUrl) {
        console.warn(
          '[Checkout QR] variantId não encontrado para a cor selecionada.',
        );
        return;
      }

      console.log('[Checkout QR] Preview', preview);
      console.log('[Checkout QR] Thumb', thumb);
      console.log('[Checkout QR] URL', checkoutUrl);

      if (checkoutQrImage) {
        checkoutQrImage.src = this.buildCheckoutQrImageUrl(checkoutUrl);
      }
      if (checkoutQrLink) {
        checkoutQrLink.href = checkoutUrl;
        checkoutQrLink.textContent = 'Abrir link do checkout';
      }

      checkoutQrOverlay.hidden = false;
    } catch (error) {
      console.error('[Checkout QR] Erro ao gerar checkout:', error);
    } finally {
      modalWrapper?.classList.remove('customizer-generated');
    }
  }

  buildTextProperties() {
    const { selectionState, bodyTextureComposer } = this.orchestrator;
    const text = selectionState.requiresText()
      ? selectionState.customText.trim()
      : '';
    const art = selectionState.getSelectedArt();
    const dualColors = this.orchestrator.usesDualTextColors();
    const customConfig = this.orchestrator.getFontCustomConfig(
      art?.textCompatible,
    );
    const font = selectionState.getSelectedFont();
    const layout = selectionState.selectedTextLayout;

    let fontFamily = font?.name || '';
    let fontSize = 0;

    if (customConfig) {
      fontFamily = `${customConfig.initial.name} + ${customConfig.name.name}`;
      fontSize = bodyTextureComposer.getFontSizeForConfig(
        customConfig.name,
        text.length,
        'fullname',
      );
    } else if (font) {
      fontSize = bodyTextureComposer.getFontSizeForConfig(
        font,
        text.length,
        layout,
      );
    }

    return {
      text,
      fontFamily,
      fontSize,
      color: dualColors
        ? selectionState.selectedNameTextColor
        : selectionState.selectedTextColor,
    };
  }

  async savePreviewImage(image) {
    const productSku =
      this.orchestrator.selectionState.getSelectedArt()?.sku ||
      this.orchestrator.DEFAULT_PRODUCT_SKU;
    const textProperties = this.buildTextProperties();
    const fallback = {
      textProperties,
      productSku,
      preview: null,
      thumb: null,
    };

    // Upload opcional: API pode estar offline — segue sem preview/thumb.
    if (!image) {
      console.warn(
        '[Preview] Sem imagem capturada; seguindo sem preview/thumb.',
      );
      return fallback;
    }

    try {
      const resSavePrint = await fetch(
        `${this.orchestrator.PRINT_API_URL}/print/upload-base64`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image,
            productSku,
            textProperties,
          }),
        },
      ).then(res => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      });

      if (resSavePrint.statusCode !== 200) {
        throw new Error(
          resSavePrint.message ||
            resSavePrint.description ||
            'Erro ao salvar case',
        );
      }

      return {
        textProperties,
        productSku,
        preview: resSavePrint.data?.original || null,
        thumb: resSavePrint.data?.thumb || null,
      };
    } catch (error) {
      console.warn(
        '[Preview] Upload indisponível; seguindo sem preview/thumb.',
        error,
      );
      return fallback;
    }
  }

  buildAddToCartPayload() {
    const { selectionState, PRODUCT_NAME, FINISH_PRICE } = this.orchestrator;
    const dualColors = this.orchestrator.usesDualTextColors();

    return {
      productName: PRODUCT_NAME,
      colorKey: selectionState.selectedColorKey,
      colorLabel: selectionState.getSelectedColor()?.label || null,
      variantId: selectionState.getSelectedColor()?.variantId || null,
      quantity: this.getFinishQuantity(),
      price: FINISH_PRICE,
      artKey: selectionState.selectedArtKey,
      text: selectionState.customText.trim() || null,
      textLayout: selectionState.selectedTextLayout,
      fontKey: selectionState.selectedFontKey,
      textColor: dualColors ? null : selectionState.selectedTextColor,
      initialTextColor: dualColors
        ? selectionState.selectedInitialTextColor
        : null,
      nameTextColor: dualColors ? selectionState.selectedNameTextColor : null,
      checkoutUrl: this.buildShopifyCheckoutUrl(),
    };
  }

  initialize() {
    const {
      modal,
      finishQtyInput,
      finishQtyMinus,
      finishQtyPlus,
      finishAddCartBtn,
      finishCheckoutQrBtn,
      checkoutQrCloseBtn,
      checkoutQrOverlay,
      reviewAccepted,
    } = this.orchestrator.dom;

    if (modal.dataset.finishReady === 'true') return;
    modal.dataset.finishReady = 'true';

    this.syncFinishStep();

    reviewAccepted?.addEventListener('change', () => {
      this.orchestrator.selectionState.accepted = reviewAccepted.checked;
      this.syncAcceptanceButtons();
    });

    finishQtyMinus?.addEventListener('click', () => {
      this.setFinishQuantity(this.getFinishQuantity() - 1);
    });

    finishQtyPlus?.addEventListener('click', () => {
      this.setFinishQuantity(this.getFinishQuantity() + 1);
    });

    finishQtyInput?.addEventListener('change', () => {
      this.setFinishQuantity(finishQtyInput.value);
    });

    finishAddCartBtn?.addEventListener('click', async () => {
      if (!this.orchestrator.selectionState.accepted) return;
      const { modalWrapper } = this.orchestrator.dom;
      modalWrapper?.classList.add('customizer-generated');

      try {
        const image =
          this.orchestrator.bottleSceneController.captureFrontPreviewImageDataUrl();

        const { preview, thumb, productSku, textProperties } =
          await this.savePreviewImage(image);
        const art = this.orchestrator.selectionState.getSelectedArt();
        const variantId =
          this.orchestrator.selectionState.getSelectedColor()?.variantId;

        console.log('[Finalizar] Adicionar ao carrinho', {
          ...this.buildAddToCartPayload(),
          productSku,
          textProperties,
          preview,
          thumb,
        });
        console.log('[Finalizar] Preview', preview);
        console.log('[Finalizar] Thumb', thumb);

        await this.orchestrator.shopifyCartController.addCustomBottleToCart({
          variantId,
          quantity: this.getFinishQuantity(),
          productSku,
          textProperties,
          preview,
          thumb,
          artName: art?.name || null,
          customization: this.buildCustomizationProperties(),
        });
      } catch (error) {
        console.error('[Finalizar] Erro ao processar case:', error);
        document.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: error.message,
            },
          }),
        );
      } finally {
        modalWrapper?.classList.remove('customizer-generated');
      }
    });

    finishCheckoutQrBtn?.addEventListener('click', () => {
      if (!this.orchestrator.selectionState.accepted) return;
      this.openCheckoutQrOverlay();
    });

    checkoutQrCloseBtn?.addEventListener('click', () => {
      this.closeCheckoutQrOverlay();
    });

    checkoutQrOverlay
      ?.querySelectorAll('[data-checkout-qr-close]')
      .forEach(element => {
        element.addEventListener('click', () => this.closeCheckoutQrOverlay());
      });
  }
}

window.FinishCheckoutController = FinishCheckoutController;
